import { Injectable, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { AppConstants } from 'src/app/constants/app.constant';
import { DataTransferObject } from '../dto/DataTransferObject';
import { LocalStorage } from '../helper/localStorage';
import { BaseService } from '../service/Base.Service';

@Injectable({
  providedIn: 'root',
})
export abstract class BaseComponent<T> implements OnInit {
  public dateFormat: string = localStorage.getItem('dateFormat');
  public router: Router;
  public companyId = localStorage.getItem('companyId');
  protected activatedRoute: ActivatedRoute;
  protected localStorage: LocalStorage;
  public LookupData: any = {};
  public SharedData: any = {};
  public lookupConfigs: any = {};
  public myForm!: FormGroup;
  public toastr: ToastrService;
  protected fb: FormBuilder;
  private _model: T = <T>{};
  private _dto: DataTransferObject<T> = <DataTransferObject<T>>{};

  get dto(): DataTransferObject<T> {
    return this._dto;
  }

  get model(): any {
    return this._model;
  }

  set model(value: any) {
    this._model = value || <T>{};
  }

  constructor(public service: BaseService<T>) {
    this.activatedRoute = AppConstants.injector.get(ActivatedRoute);
    this.router = AppConstants.injector.get(Router);
    this.localStorage = AppConstants.injector.get(LocalStorage);
    this.fb = AppConstants.injector.get(FormBuilder);
    this.toastr = AppConstants.injector.get(ToastrService);
  }

  protected async initialization() { }

  protected async beforeDisplay() { }

  protected async afterDisplay() { }

  protected async beforeClear() { }

  protected async afterClear() { }

  protected async beforeSave() { }

  protected async afterSave() { }

  ngOnInit(): void {
    this.initialization();
  }

  public setFormValues() {
    Object.keys(this.myForm.value).forEach((name) => {
      if (this.model && this.model[name]) {
        const value = this.model[name];
        const ctrl = this.myForm.controls[name];
        ctrl.setValue(value);
      } else {
        this.myForm.controls[name].setValue(undefined);
      }
    });
  }

  async clear() {
    await this.beforeClear();
    Object.keys(this.myForm.controls).map((controlName) => {
      let ctr = this.myForm.get(controlName);
      if (ctr) {
        ctr.markAsUntouched({ onlySelf: true });
        ctr.markAsPristine({ onlySelf: true });
      }
    });
    this.model = <T>{};
    this.setFormValues();
    await this.afterClear();
  }

  public showError(message: string | Array<string>, title: string = 'Error') {
    let msg = typeof message === 'string' ? message : message?.join('</br>');
    this.toastr.error(msg, title, {
      enableHtml: true,
      tapToDismiss: true,
      progressBar: true,
      progressAnimation: 'increasing',
      disableTimeOut: false,
      timeOut: 6000,
      easeTime: 300,
    });
  }

  public showSuccess(message: string | Array<string>, title: string = 'Success') {

    let msg = typeof message === 'string' ? message : message?.join('</br>');
    this.toastr.success(msg, title, {
      enableHtml: true,
      tapToDismiss: true,
      progressBar: true,
      progressAnimation: 'increasing',
      disableTimeOut: false,
      timeOut: 6000,
      easeTime: 300,
    });
  }

  public showInfo(message: string | Array<string>, title: string = 'Info') {
    let msg = typeof message === 'string' ? message : message?.join('</br>');
    this.toastr.info(title, msg, {
      enableHtml: true,
      tapToDismiss: true,
      progressBar: true,
      progressAnimation: 'increasing',
      disableTimeOut: false,
      timeOut: 6000,
      easeTime: 300,
    });
  }

  public showWarning(
    message: string | Array<string>,
    title: string = 'Warning'
  ) {
    let msg = typeof message === 'string' ? message : message?.join('</br>');

    this.toastr.warning(title, msg, {
      enableHtml: true,
      tapToDismiss: true,
      progressBar: true,
      progressAnimation: 'increasing',
      disableTimeOut: false,
      timeOut: 6000,
      easeTime: 300,
    });
  }

  public validateForm(): boolean {
    let isValid: boolean = false;

    if (this.myForm) {
      this.markFormGroupTouched(this.myForm);
    }

    return isValid;
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(this.myForm.controls).forEach((key) => {
      this.myForm.get(key).markAsDirty({ onlySelf: true });
      this.myForm.get(key).markAsTouched({ onlySelf: true });
      this.myForm.get(key).markAsPristine({ onlySelf: true });
    });
  }
}
